/** @format */
import { Formik } from 'formik';
import { useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

import Master from '../Layouts/Master';
import useApi from '../../Shared/hooks/api/Index';
import { error, success } from '../../Shared/Components/Toast';
import { EventContext } from '../../Contexts/EventContext';
import ErrorMessage from '../../Shared/Components/ErrorMessage';
import FileUploader from '../../Shared/Components/FileUploader';
import { usePasswordPolicy } from '../../Shared/hooks/usePasswordPolicy';
import { baseApiUrl } from '../../Shared/utils/helper';
import { buildNewPasswordFieldSchema } from '../../Shared/utils/passwordPolicy';
import { getStoredAuthToken } from '../../Shared/utils/authToken';
import Loading from '../../Shared/Components/Loading';

const Create = () => {
	const passwordPolicy = usePasswordPolicy();
	const validationSchema = useMemo(
		() =>
			Yup.object().shape({
				first_name: Yup.string()
					.required('نام الزامی است')
					.max(190, 'نام نمیتواند بیشتر از 190 کاراکتر باشد'),
				last_name: Yup.string()
					.required('نام خانوادگی الزامی است')
					.max(190, 'نام خانوادگی نمیتواند بیشتر از 190 کاراکتر باشد'),
				gender: Yup.string().required('جنسیت الزامی است'),
				province_id: Yup.string().required('انتخاب استان الزامی است'),
				nat_code: Yup.string()
					.required('کد ملی الزامی است')
					.min(10, 'کد ملی باید 10 رقم باشد')
					.max(10, 'کد ملی باید 10 رقم باشد'),
				mobile: Yup.string()
					.required('موبایل الزامی است')
					.min(11, 'موبایل باید 11 رقم باشد')
					.max(11, 'موبایل باید 11 رقم باشد'),
				password: buildNewPasswordFieldSchema(passwordPolicy),
			}),
		[passwordPolicy],
	);

	const navigate = useNavigate();
	const eventContext = useContext(EventContext);
	const [loading, setLoading] = useState(false);
	const [photoIds, setPhotoIds] = useState([]);
	const [backendErrors, setBackendErrors] = useState({});
	const [submitFormLoading, setSubmitFormLoading] = useState(false);

	useEffect(() => {
		const getProvinces = async () => {
			setLoading(true);
			await useApi.get(
				'/province',
				{},
				(res) => {
					let provinces = [...res.data.data];
					let last = provinces?.pop();
					provinces?.unshift(last);
					eventContext.provinces = provinces;
					setLoading(false);
				},
				(err) => {
					setLoading(false);
					error('خطا در دریافت اطلاعات');
				},
			);
		};
		getProvinces();
	}, []);

	const onSubmitForm = async (values, setSubmitting) => {
		if (photoIds.length > 0) {
			let photoId = photoIds[0].serverId;
			let avatar = JSON.parse(photoId);
			values['avatar'] = avatar.data.path;
		}

		setSubmitFormLoading(true);

		await useApi.post(
			'/user/register',
			values,
			(res) => {
				success('کاربر با موفقیت ایجاد شد');
				navigate('/panel/user', { replace: true });
			},
			(err) => {
				setSubmitFormLoading(false);
				if (err?.status === 422) {
					let errors = {};
					let errorsFields = err?.data?.errors;
					Object.entries(errorsFields)?.forEach((el) => {
						errors[el[0]] = el[1][0];
					});
					setBackendErrors(errors);
				} else {
					error('خطایی در ثبت اطلاعات پیش آمده است.');
				}
			},
		);
	};

	return loading ? (
		<Loading />
	) : (
		<Master>
			<Formik
				initialValues={{
					first_name: '',
					last_name: '',
					nat_code: '',
					mobile: '',
					province_id: '',
					password: '',
					avatar: '',
					gender: '',
				}}
				onSubmit={(values, { setSubmitting }) => {
					onSubmitForm(values, setSubmitting);
				}}
				validationSchema={validationSchema}>
				{({
					values,
					errors,
					handleChange,
					handleSubmit,
					isSubmitting,
					setFieldValue,
				}) => (
					<form onSubmit={handleSubmit}>
						<div className='row border rounded p-2 mt-3'>
							<div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
								<div className='form-group'>
									<div className='d-flex flex-column mt-1 mx-2'>
										<label>نام</label>
										<input
											name='first_name'
											value={values.first_name}
											onChange={handleChange}
											type='text'
											className='form-control'
										/>
										<ErrorMessage error={errors.first_name} />
									</div>
								</div>
							</div>
							<div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
								<div className='form-group'>
									<div className='d-flex flex-column mt-1 mx-2'>
										<label>نام خانوادگی</label>
										<input
											name='last_name'
											value={values.last_name}
											onChange={handleChange}
											type='text'
											className='form-control'
										/>
										<ErrorMessage error={errors.last_name} />
									</div>
								</div>
							</div>

							<div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
								<div className='form-group'>
									<label>جنسیت</label>
									<select
										className='form-control'
										name='gender'
										value={values.gender}
										onChange={handleChange}>
										<option>انتخاب کنید</option>
										<option value='1'>مرد</option>
										<option value='2'>زن</option>
									</select>
									<ErrorMessage error={errors.gender} />
								</div>
							</div>
							<div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
								<div className='form-group'>
									<div className='d-flex flex-column mt-1 mx-2'>
										<label>موبایل</label>
										<input
											name='mobile'
											value={values.mobile}
											onChange={handleChange}
											type='text'
											className='form-control'
										/>
										<ErrorMessage
											error={
												backendErrors?.mobile !== undefined
													? backendErrors?.mobile
													: errors.mobile
											}
										/>
									</div>
								</div>
							</div>
							<div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
								<div className='form-group'>
									<div className='d-flex flex-column mt-1 mx-2'>
										<label>کد ملی</label>
										<input
											name='nat_code'
											value={values.nat_code}
											onChange={handleChange}
											type='text'
											className='form-control'
										/>
										<ErrorMessage
											error={
												backendErrors?.nat_code !== undefined
													? backendErrors?.nat_code
													: errors.nat_code
											}
										/>
									</div>
								</div>
							</div>
							<div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
								<div className='form-group'>
									<label>استان </label>
									<select
										className='form-control'
										name='province_id'
										value={values.province_id}
										onChange={handleChange}>
										<option>انتخاب کنید</option>

										{eventContext.provinces?.map((item) => (
											<option key={item.id} value={item.id}>
												{item.name}
											</option>
										))}
									</select>
									<ErrorMessage error={errors.province_id} />
								</div>
							</div>
							<div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
								<div className='form-group'>
									<div className='d-flex flex-column mt-1 mx-2'>
										<label>کلمه عبور</label>
										<input
											name='password'
											value={values.password}
											onChange={handleChange}
											type='password'
											className='form-control'
										/>
										<ErrorMessage error={errors.password} />
									</div>
								</div>
							</div>
							<div className='col-12 col-sm-12 col-md-6 col-lg-6 col-xl-6'>
								<div className='form-group'>
									<label>تصویر</label>
									<FileUploader
										files={photoIds}
										roles={['image/jpeg', 'image/jpg', 'image/png']}
										setFiles={setPhotoIds}
										allowMultiple={false}
										maxFiles={1}
										maxFileSize='1024KB'
										serverUrl={`${baseApiUrl()}/user/image-upload`}
										method='POST'
										headers={{
											Accept: 'application/json',
											Authorization: getStoredAuthToken(),
										}}
									/>
								</div>
							</div>
						</div>
						<div className='col-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 my-3'>
							<div className='d-flex flex-row justify-content-end'>
								<button className='btn btn-info btn-sm f-14'>
									{submitFormLoading ? (
										<div
											className='spinner-border spinner-border-sm text-white mb-1'
											role='status'></div>
									) : (
										'ثبت کاربر'
									)}
								</button>
							</div>
						</div>
					</form>
				)}
			</Formik>
		</Master>
	);
};

export default Create;
